import React, {useEffect, useState, useRef, useContext} from 'react';
import {useHistory} from 'react-router-dom';
import {Button, Row, Col} from 'reactstrap';
import {toast} from 'react-toastify';
import ArticleList from './ArticleList';
import ArticleListBySource from './ArticleListBySource';
import {EntityContext} from 'contexts/EntityContext';
import Loader from 'components/Loader';
import {
    getEntityStatsById, getEntityNewsById, getEntityCSV,
    updateEntitySourceFilter, updateEntityNewsSorting,
} from 'utils/api/entitiesApi';
import Wordcloud from 'components/Wordcloud';
import {ErrorContext} from 'contexts/ErrorContext';
import VolumeChartSmall from './Charts/VolumeChartSmall';
import EmptySentimentChart from './Charts/EmptySentimentChart';
import EmptyVolumeChart from './Charts/EmptyVolumeChart';
import SentimentChartSmall from './Charts/SentimentChartSmall';
import EntityFilters from './EntityFilters';
import './style.scss';

const EntitiesView = (props) => {
    let history = useHistory();
    const {
        activeEntity, entitySocialFilters, entitySentimentFilters, activeEntitySort, entitySentimentCount,
        setActiveEntitySort, setEntitySentimentCount, entitySocialCount, setEntitySocialCount, setUpdatingEntityCount,
        activeEntityStats, setActiveEntityStats, setEntitySocialFilters,
    } = useContext(EntityContext);
    const {handleErrors} = useContext(ErrorContext);

    const {entitiesLoading, entityNews, setEntityNews, auth} = props;

    const [fetchingEntityStats, setFetchingEntityStats] = useState(true);
    const [fetchingEntity, setFetchingEntity] = useState(true);
    const [entityData, setEntityData] = useState(null);
    const [updatingEntity, setUpdatingEntity] = useState(false);
    const [wordCloudData, setWordCloudData] = useState(null);
    const [hideChartBasedOnResults, setHideChartBasedOnResults] = useState(false);
    const [allowedProductCharts, setAllowedProductCharts] = useState([]);
    const [totalCount, setTotalCount] = useState(null);

    const [end, setEnd] = useState(50);
    const [heightForVirtualization, setHeightForVirtualization] = useState('0px');
    const [scrollPositionForAdjusting, setScrollPositionForAdjusting] = useState(0);
    const newsContainer = useRef(null);

    useEffect(() => {  // Load entity data after selection
        if(activeEntity) {
            getEntityById();
            getEntityStats();
        }
    }, [activeEntity]);


    useEffect(() => {  // Set virtualization params
        if(entityNews && newsContainer && newsContainer.current) {
            setHeightForVirtualization(getDynamicalHeight(entityNews) + 'px');
            setScrollPositionForAdjusting(getDynamicalHeight(entityNews.slice(0, 50)) - newsContainer.current.clientHeight);
            setEnd(50);
            newsContainer.current.scrollTop = 0;
        }
    }, [entityNews]);

    useEffect(() => {  // Determine which charts to display
        if(activeEntityStats) {
            let allowedCharts = [];
            activeEntityStats.hasOwnProperty('yakeKeywors') && allowedCharts.push('keywords');
            activeEntityStats.hasOwnProperty('countVolume') && allowedCharts.push('volume');
            activeEntityStats.hasOwnProperty('chartSmaPositive') && allowedCharts.push('sentiment');
            setAllowedProductCharts(allowedCharts);
        }
    }, [activeEntityStats]);

    const listVirt = () => {
        if(entityNews && (newsContainer.current.scrollTop > scrollPositionForAdjusting)) {
            setScrollPositionForAdjusting(scrollPositionForAdjusting + getDynamicalHeight(entityNews.slice(end, end + 50)));
            setEnd(end + 50);
        }
    };

    const getDynamicalHeight = (list) => {
        let finalHeight = 0;
        let listArr = list ? list : [];
        listArr.forEach(listItem => {
            if(listItem.title && listItem.title.length < 100) {
                finalHeight += 93;
            } else {
                finalHeight += 119;
            }
            if(listItem.abstract && listItem.abstract.trim() !== '') {
                if(listItem.abstract.length < 140) {
                    finalHeight += 21;
                } else if(listItem.abstract.length > 140 && listItem.abstract.length < 280) {
                    finalHeight += 42;
                } else {
                    finalHeight += 63;
                }
            }
        });
        return finalHeight;
    };

    const getEntityById = async(updating) => {
        try {
            // updating ? setUpdatingEntity(true) : setFetchingEntity(true);
            !updating && setFetchingEntity(true);
            setHideChartBasedOnResults(false);
            let entityRes = await getEntityNewsById(activeEntity.id);
            let articleList = (entityRes.content && entityRes.content.result) ? entityRes.content.result : [];
            if(entityRes.content.entityData.settings.sort !== 2) {
                articleList.length < 5 && setHideChartBasedOnResults(true);
            } else {
                if(articleList.length > 0) {
                    let count;
                    articleList.forEach(source => {
                        count += source.news.length;
                    });
                    if(count < 5) {
                        setHideChartBasedOnResults(true);
                    } else {
                        setHideChartBasedOnResults(false);
                    }
                } else {
                    setHideChartBasedOnResults(true);
                }
            }
            let settingsObj = entityRes.content.entityData.settings;
            setActiveEntitySort(settingsObj.sort);
            let defaultEntitySourceObj = {
                'twitter': null,
                'facebook': null,
                'youtube': null,
                'rss': null,
                'web': null,
                'google_ozmosys': null,
                'instagram': null,
            };
            for(const property in settingsObj.sources) {
                if(settingsObj.sources[property] !== null) defaultEntitySourceObj[property] = settingsObj.sources[property];
            }
            // articleList.length > 0 && articleList[0].hasOwnProperty('news') && setActiveEntitySort(2);
            setEntitySocialFilters(defaultEntitySourceObj);
            setEntityNews(articleList);
            setEntityData(entityRes.content.entityData);
            // updating ? setUpdatingEntity(false) : setFetchingEntity(false);
            !updating && setFetchingEntity(false);
        } catch(err) {
            console.log(err);
            updating ? setUpdatingEntity(false) : setFetchingEntity(false);
            handleErrors(err);
        }
    };

    const getEntityStats = async() => {
        try {
            setFetchingEntityStats(true);
            let statsData = await getEntityStatsById(activeEntity.id);
            if(statsData.content.yakeKeywors) {
                let wordArr = [];
                statsData.content.yakeKeywors.forEach(item => {
                    if(item.hasOwnProperty('coef')) {
                        wordArr.push({text: item.keyword, value: (1 / item.coef)});
                    } else if(item.hasOwnProperty('score'))
                        wordArr.push({text: item.keyword, value: (1 / item.score)});
                });
                setWordCloudData(wordArr);
            }
            if(statsData.content.newsStatisticsBySource) {
                applySourceCount(statsData.content.newsStatisticsBySource);
                setTotalCount(statsData.content.newsStatisticsBySource.countNews);
            }
            setActiveEntityStats(statsData.content);
            setFetchingEntityStats(false);
        } catch(err) {
            console.log(err);
            handleErrors(err);
            setFetchingEntityStats(false);
        }
    };

    const applySourceCount = (countData) => {
        let sourceCountObj = {
            twitter: 0,
            facebook: 0,
            youtube: 0,
            rss: 0,
            web: 0,
            google_ozmosys: 0,
            instagram: 0,
        };
        for(const property in countData) {
            if(sourceCountObj.hasOwnProperty(property)) {
                sourceCountObj[property] = countData[property];
            }
        }
        setEntitySocialCount(sourceCountObj);
    };

    const fetchEntityCSVFile = async() => {
        try {
            await getEntityCSV(activeEntity.id);
        } catch(err) {
            console.log(err);
            if(err.message && err.message.length < 150) {
                let message = 'Unable to fetch CSV data for this entity';
                toast.error(message);
            }
        }
    };

    const toggleSocialFilters = async(source) => {
        try {
            if(updatingEntity) return;
            setUpdatingEntity(true);
            let socialFiltersClone = JSON.parse(JSON.stringify(entitySocialFilters));
            socialFiltersClone[source] = !socialFiltersClone[source];
            setEntitySocialFilters(socialFiltersClone);

            if(activeEntitySort !== 2) {
                const newResults = await updateEntitySourceFilter(activeEntity.id, source);
                await getEntityById(true);
                if(Array.isArray(newResults.content.result)) {
                    setEntityNews(newResults.content.result);
                }
            } else {
                updateEntitySourceFilter(activeEntity.id, source);
            }
            setUpdatingEntity(false);
        } catch(err) {
            handleErrors(err);
            console.log(err);
            setUpdatingEntity(false);

        }
    };

    const handleEntitySortChange = async(value) => {
        try {
            setUpdatingEntity(true);
            let numValue = parseInt(value);
            setActiveEntitySort(numValue);
            const newEntityNewsResults = await updateEntityNewsSorting(activeEntity.id, numValue);
            setEntityNews(newEntityNewsResults.content.result);
            setUpdatingEntity(false);
        } catch(err) {
            console.log(err);
            handleErrors(err);
            setUpdatingEntity(false);
        }
    };

    if(fetchingEntityStats || entitiesLoading || fetchingEntity) {
        return (
            <div className="d-flex justify-content-center align-items-center h-50" style={{padding: '5rem 0'}}>
                <Loader small text="Fetching entity data"/>
            </div>
        );
    }
    if(!activeEntity || !entityData) {
        return <div className="p-4">No entities have been created yet.</div>;
    }
    return (
        <div className="home-page__center-inner home-page-entity" ref={newsContainer} onScroll={(e) => listVirt(e)}>
            <div className="home-page-entity__header d-flex align-items-center justify-content-between"
                 style={{padding: '0 15px 10px 0'}}>
                <div className="d-flex">
                    <div style={{width: '50px', height: '50px', minWidth: '50px'}}>
                        {activeEntity.logoUrl ?
                            <div
                                className="icon-with-letter"
                                style={{
                                    background: `url(${activeEntity.logoUrl}) center center / contain no-repeat`,
                                    backgroundSize: 'contain',
                                }}
                            />
                            :
                            <div className="icon-with-letter" style={{backgroundColor: activeEntity.color}}>
                                {activeEntity.title && activeEntity.title[0]}
                            </div>
                        }
                    </div>
                    <div
                        className="d-flex flex-column justify-content-center"
                        style={{marginLeft: '15px'}}>
                        <div style={{fontWeight: '700', fontSize: '18px'}}>
                            {activeEntity.title}
                        </div>
                        <div>
                            {activeEntity.domain ? activeEntity.domain : ''}
                        </div>
                    </div>
                </div>
                <div>
                    {activeEntity.status !== 2 &&
                    <div className="d-flex align-items-center">
                        <div className="mr-2"
                             style={{height: '14px', width: '14px', backgroundColor: '#f1c231', borderRadius: '50%'}}/>
                        <span>Sources pending</span>
                    </div>
                    }
                </div>
            </div>
            {(entityData.status === 0 ||
                (entityData.status === 1 && Object.keys(entitySocialFilters).filter((key) => entitySocialFilters[key] !== null).length === 0)) ?
                <div style={{borderTop: '1px solid #c8ced3', paddingTop: '10px', marginBottom: '2rem'}}>
                    <div>Sources are currently pending for this entity.</div>
                    <div>This page will be updated automatically as sources become available.</div>
                </div>
                :
                Object.keys(entitySocialFilters).filter((key) => entitySocialFilters[key] !== null).length === 0 ?
                    <div style={{borderTop: '1px solid #c8ced3', paddingTop: '10px'}}>
                        <div>This entity has no assigned sources.</div>
                        <Button onClick={() => history.push(`/edit-entity/${activeEntity.id}`)}
                                className="calltoaction-button mt-3">
                            Add Sources
                        </Button>
                    </div>
                    :
                    <>
                        <div>
                            <div className="d-flex justify-content-between"
                                 style={{borderTop: '1px solid #c8ced3', borderBottom: '1px solid #c8ced3'}}
                            >
                                <Row className="flex-grow-1">
                                    {allowedProductCharts.includes('volume') &&
                                    <Col md={12} lg={12} xl={(12 / allowedProductCharts.length)}>
                                        {activeEntityStats ?
                                            <VolumeChartSmall activeEntityStats={activeEntityStats}/>
                                            :
                                            <EmptyVolumeChart/>
                                        }
                                    </Col>
                                    }
                                    {allowedProductCharts.includes('keywords') &&
                                    <Col md={12} lg={12} xl={(12 / allowedProductCharts.length)}>
                                        <div className="d-flex flex-column"
                                             style={{paddingTop: '15px', paddingLeft: '10px'}}>
                                            <div className="entity-chart-title">Keywords</div>
                                            {activeEntityStats && activeEntityStats.yakeKeywors ?
                                                <div className="wordcloud-container"
                                                     style={{height: '260px', display: 'flex', alignItems: 'center'}}>
                                                    {/*<Wordcloud words={activeEntityStats.yakeKeywors}/>*/}
                                                    <Wordcloud words={wordCloudData}/>
                                                </div>
                                                :
                                                <div
                                                    style={{height: '260px', fontWeight: '500'}}
                                                    className="d-flex align-items-center justify-content-center">
                                                    Not yet enough articles to detect keywords
                                                </div>
                                            }
                                        </div>
                                    </Col>
                                    }
                                    {allowedProductCharts.includes('sentiment') &&
                                    <Col md={12} lg={12} xl={(12 / allowedProductCharts.length)}>
                                        <div>
                                            <div className="pl-3 pr-3">
                                                {hideChartBasedOnResults || !activeEntityStats ?
                                                    <EmptySentimentChart hideBasedOnResultsLength/>
                                                    :
                                                    <SentimentChartSmall
                                                        activeEntityStats={activeEntityStats}
                                                        datesData={activeEntityStats.chartDates || []}
                                                        SmaDataPositive={activeEntityStats.chartSmaPositive || []}
                                                        SmaDataNeutral={activeEntityStats.chartSmaNeutral || []}
                                                        SmaDataNegative={activeEntityStats.chartSmaNegative || []}
                                                        fetchEntityCSVFile={fetchEntityCSVFile}
                                                    />
                                                }
                                            </div>
                                        </div>
                                    </Col>
                                    }
                                </Row>
                            </div>
                        </div>
                        {entityNews !== null && !fetchingEntity &&
                        <div className="w-100">
                            <div style={{maxWidth: '1000px', margin: '0 auto'}}>
                                <EntityFilters
                                    searchResultsLength={entityNews.length}
                                    totalCount={totalCount}
                                    updatingFilters={updatingEntity}
                                    activeSort={activeEntitySort}
                                    activeEntity={activeEntity}
                                    toggleSocialFilters={toggleSocialFilters}
                                    handleEntitySortChange={handleEntitySortChange}
                                    auth={auth}
                                />
                                {entityNews.length > 0 && entityNews[0].hasOwnProperty('news') ?
                                    <ArticleListBySource
                                        searchResults={entityNews}
                                        socialFilters={entitySocialFilters}
                                        sentimentFilters={entitySentimentFilters}
                                        activeEntityId={activeEntity.id}
                                        setEntityNews={setEntityNews}
                                        socialCount={entitySocialCount}
                                        sentimentCount={entitySentimentCount}
                                        setSentimentCount={setEntitySentimentCount}
                                        setUpdatingCount={setUpdatingEntityCount}
                                        handleEntitySortChange={handleEntitySortChange}
                                        toggleSocialFilters={toggleSocialFilters}
                                        updatingFilters={updatingEntity}
                                        totalCount={totalCount}
                                    />
                                    :
                                    <ArticleList
                                        searchResults={entityNews}
                                        socialFilters={entitySocialFilters}
                                        sentimentFilters={entitySentimentFilters}
                                        activeSort={activeEntitySort}
                                        heightForVirtualization={heightForVirtualization}
                                        end={end}
                                        socialCount={entitySocialCount}
                                        sentimentCount={entitySentimentCount}
                                        setSentimentCount={setEntitySentimentCount}
                                        setUpdatingCount={setUpdatingEntityCount}
                                        handleEntitySortChange={handleEntitySortChange}
                                        toggleSocialFilters={toggleSocialFilters}
                                        updatingFilters={updatingEntity}
                                        totalCount={totalCount}
                                    />
                                }
                            </div>
                        </div>
                        }
                    </>
            }
        </div>
    );
};

export default EntitiesView;
